import React, { useState } from "react";
import dayjs from "dayjs";

import {
  Grid,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { formatDate } from "@aclymatepackages/formatters";

import Card from "../../atoms/mui/Card";

import {
  useAccountData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";
import { useQuickbooks } from "../../../helpers/integrations/quickbooks";
import { useSubscriptionType } from "../../../helpers/hooks/companyData";
import usePermissions from "../../../helpers/hooks/usePermissions";

const QuickbooksCard = ({ gridProps = { sm: 12 } }) => {
  const [{ status: subscriptionStatus }] = useSubscriptionType("saas");
  const { canEdit } = usePermissions();

  const [quickbooksError, setQuickbooksError] = useState("");

  const {
    pullDate,
    taskStatus,
    refreshTokenExpiration,
    tokenLoading,
    launchQuickbooks,
    fetchQuickbooksData,
    currentTask,
    archived,
    id: tokenDocId
  } = useQuickbooks({ setQuickbooksError });
  const [{ integrations: integrationsArray = [] }] = useAccountData();
  const { updateAccountData, updateCollectionDoc } = useCachedFirebaseCrud();

  const isRefreshTokenExpired = dayjs().isAfter(dayjs(refreshTokenExpiration));
  const isPulling = taskStatus === "uploading" || taskStatus === "running";
  const isPullingError = taskStatus === "error";
  const isTaskFinished = taskStatus === "finished";

  const quickbooksSubtitle = () => {
    if (isPulling) {
      if (currentTask?.status === "uploading") {
        return "Fetching your QuickBooks transactions. This may take a few minutes depending on your data size.";
      }
      if (currentTask?.status === "running") {
        const progress =
          currentTask.currentChunk && currentTask.totalChunks
            ? `Processing ${currentTask.currentChunk}/${currentTask.totalChunks} batches.`
            : "Processing your transactions.";
        return `${progress} Almost done!`;
      }
      return "Your QuickBooks data is loading. Depending on how much data you have, this could take a few minutes.";
    }
    if (!pullDate || archived) {
      return "We can connect to your QuickBooks account for faster setup";
    }
    if (isRefreshTokenExpired) {
      return "Your QuickBooks login has expired. Reconnect it to resume data syncs from your account.";
    }
    if (isTaskFinished && currentTask) {
      const count =
        currentTask.totalTasksProcessed || currentTask.totalTasks || 0;
      return `Last sync completed with ${count} transactions. Data from synced QuickBooks accounts is automatically pulled daily at 6 AM.`;
    }
    return `You last pulled data from QuickBooks on: ${formatDate(
      pullDate
    )}. Data from synced QuickBooks accounts is automatically pulled daily at 6 AM.`;
  };

  const onArchiveQuickbooks = async () => {
    await updateCollectionDoc("integrations", tokenDocId, { archived: true });
    return updateAccountData({
      integrations: integrationsArray.filter(
        (integration) => integration !== "quickbooks"
      )
    });
  };

  return (
    <Card
      gridProps={gridProps}
      isLoading={tokenLoading}
      cardType="layout"
      title="Quickbooks"
      subtitle={quickbooksSubtitle()}
      icon={<ReceiptIcon />}
      action={
        canEdit &&
        pullDate &&
        !archived && (
          <Tooltip title="Disconnect QuickBooks">
            <IconButton onClick={onArchiveQuickbooks}>
              <LinkOffIcon />
            </IconButton>
          </Tooltip>
        )
      }
      content={
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid item>
            {isPulling ? (
              <CircularProgress />
            ) : !pullDate || isRefreshTokenExpired || archived ? (
              <Button
                onClick={() => launchQuickbooks()}
                disabled={subscriptionStatus !== "active" || !canEdit}
              >
                <img src="/images/quickbooksButton.png" alt="Quickbooks Icon" />
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchQuickbooksData()}
                disabled={!canEdit}
              >
                Fetch Latest Data
              </Button>
            )}
          </Grid>
          {(quickbooksError || isPullingError) && (
            <Grid item>
              <Typography variant="body2" align="center" color="error">
                {isPullingError
                  ? "There was an error pulling your Quickbooks data. Contact support for more details."
                  : quickbooksError}
              </Typography>
            </Grid>
          )}
        </Grid>
      }
    />
  );
};

export default QuickbooksCard;
